import React, { SetStateAction, useEffect, useMemo } from "react";
import { Button, Checkbox, Col, Flex, Form, Grid, Input, Popconfirm, Row, Tag, Tooltip } from "antd";

import { useForm } from "antd/es/form/Form";
import { ResponsiveAntdTable } from "../../../../../Components/ResponsiveAntdTable";
import { useQuery } from "@tanstack/react-query";
import { SelectComPesquisa } from "../../../../../Components/SelectComPesquisa";
import { TypeAlunoEResponsavel } from "../../../../../Models/Domain/Pessoa/AlunoEResponsavel";
import { Ordenar } from "../../../../../Utils/Ordenacao";
import { Conjunto } from "../../../../../Components/Conjunto";
import { ResponsavelService } from "../../../../../Services/Domain/Pessoa/ResponsavelService";

interface IFormularioDeResponsaveisDoAluno {
    responsaveisAPI: [ TypeAlunoEResponsavel[], React.Dispatch<SetStateAction<TypeAlunoEResponsavel[]>> ],
}

export const FormularioDeResponsaveisDoAluno: React.FC<IFormularioDeResponsaveisDoAluno> = (props) => {

    const tamanhoDaTela = Grid.useBreakpoint();

    const [responsaveis, setResponsaveis] = props.responsaveisAPI;
    const [ form ] = useForm<TypeAlunoEResponsavel>()

    const responsaveisDisponiveis = useQuery({
        queryKey: ["listarResponsaveis"],
        queryFn: () => new ResponsavelService().listar(),
    })

    const principalJaEscolhido = useMemo(() => responsaveis.find((r) => r.principal == true) != undefined, [responsaveis])
    
    const onFinish = (valores: TypeAlunoEResponsavel) => {
        if(valores.responsavel.codigo != undefined){
            const org = responsaveisDisponiveis.data?.data.find((v) => v.codigo == valores.responsavel.codigo);
            if (org) valores.responsavel = org;
        }

        setResponsaveis((prev) => [...prev, valores])
        form.resetFields();
    }

    return <React.Fragment>

        <Flex vertical style={{width: '100%'}}>

            <Form<TypeAlunoEResponsavel> form={form} onFinish={onFinish} autoComplete="off" layout="vertical" validateTrigger="onBlur">
                <Row gutter={16}>
                    <Col xs={24} md={24} lg={19}>
                        <Form.Item<TypeAlunoEResponsavel> 
                            name={["responsavel", "codigo"]} 
                            label="Nome"
                            rules={[
                                {
                                    required: true,
                                    message: "O responsável é obrigatório"
                                }
                            ]}
                        >
                            <SelectComPesquisa
                                style={{ width: "100%" }}
                                loading={responsaveisDisponiveis.isFetching}
                                allowClear
                                placeholder="Selecione o responsável"
                                options={
                                    responsaveisDisponiveis.data?.data.filter((r) => responsaveis.find((rr) => r.codigo == rr.responsavel.codigo) == undefined)
                                        .map((r) => ({
                                            value: r.codigo,
                                            label: r.pessoa?.nome,
                                        }))
                                }
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} md={24} lg={2} >
                        <Tooltip placement="bottomLeft" title={principalJaEscolhido ? <> O aluno só pode ter um responsável principal </> : undefined} >
                            <Form.Item<TypeAlunoEResponsavel>
                                name={["principal"]} valuePropName="checked" label="Principal?"
                                style={{ marginBottom: "1em" }}
                            >
                                <Checkbox disabled={principalJaEscolhido} >Sim</Checkbox>
                            </Form.Item>
                        </Tooltip>
                    </Col>

                    <Col xs={24} md={24} lg={3} style={{ display: "flex", gap: "16px", width: "100%" }}>
                        <Form.Item label={tamanhoDaTela.lg? <></> : undefined}>
                            <Button type="primary" key="submit" htmlType='submit' style={{ flex: 1 }}>
                                Adicionar
                            </Button>
                        </Form.Item>

                    </Col>
                </Row>

            </Form>

            <Row>
                <Col xs={24}>
                    <ResponsiveAntdTable<TypeAlunoEResponsavel>
                        bordered
                        locale={{ emptyText: 'Nenhum responsável adicionado' }}
                        dataSource={responsaveis}
                        rowKey={(k) => k.responsavel.codigo!}
                        addTitleOn="lg"
                        columns={[
                           {
                                title: "Nome",
                                dataIndex: ["responsavel", "pessoa", "nome"],
                                key: "responsavel-nome",
                                breakpoints: { xs: 24, lg: 10 },
                                sorter: (a, b) => Ordenar.String(a.responsavel.pessoa?.nome, b.responsavel.pessoa?.nome),
                            },
                            {
                                title: "Principal",
                                dataIndex: ["principal"],
                                key: "responsavel-principal",
                                breakpoints: { sm: 24, lg: 8 },
                                sorter: (a, b) => Ordenar.Booleano(a.principal, b.principal),
                                render: (v) => (v ? <Tag style={{ width: "fit-content" }} color="green">Sim</Tag> : <Tag style={{ width: "fit-content" }} color="red">Não</Tag>)
                            },
                            {
                                title: "Ações",
                                key: "acoes-responsavel",
                                breakpoints: { sm: 24, lg: 6 },
                                render: (_, item) => {
                                    return <Conjunto
                                        itens={(() => {
                                            const btnStyle: React.CSSProperties = { textWrap: "wrap", height: "fit-content", 'overflowWrap': "anywhere" }

                                            const excluir = (
                                                <Popconfirm
                                                    title='Atenção'
                                                    description='Tem certeza que deseja remover este responsável?'
                                                    okText='Confirmar'
                                                    cancelText='Cancelar'
                                                    onConfirm={() => {
                                                        setResponsaveis((prev) => prev.filter((r) => {
                                                            return r.responsavel.codigo != item.responsavel.codigo
                                                        }))
                                                    }}
                                                >
                                                    <Button
                                                        danger
                                                        size="small"
                                                        style={btnStyle}
                                                    >
                                                        Excluir
                                                    </Button>
                                                </Popconfirm>

                                            )

                                            const adicionarPrincipal = (
                                                <Button
                                                    type="primary"
                                                    size="small"
                                                    style={btnStyle}
                                                    onClick={() => {
                                                        setResponsaveis((prev) => prev.map((r) => {
                                                            return r.responsavel.codigo != item.responsavel.codigo ? r : { ...r, principal: true }
                                                        }))
                                                    }}
                                                >
                                                    Marcar principal
                                                </Button>
                                            )

                                            const removerPrincipal = (
                                                <Popconfirm
                                                    title='Atenção'
                                                    description='Tem certeza que deseja desmarcar o responsável principal?'
                                                    okText='Confirmar'
                                                    cancelText='Cancelar'
                                                    onConfirm={() => {
                                                        setResponsaveis((prev) => prev.map((r) => {
                                                            return r.responsavel.codigo != item.responsavel.codigo ? r : { ...r, principal: false }
                                                        }))
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        style={btnStyle}
                                                        danger
                                                    >
                                                        Remover principal
                                                    </Button>
                                                </Popconfirm>
                                            )

                                            if (item.principal == true) return [excluir, removerPrincipal]
                                            if (principalJaEscolhido == false) return [excluir, adicionarPrincipal]
                                            return [excluir]
                                        })()}
                                    />
                                }
                            },
                        ]}
                    />
                </Col>
            </Row>

        </Flex>

    </React.Fragment>
}
